import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { tocLobbyRiveUrl } from '../../../../services/ui';
import { useStores } from '../../../../stores/useStores';

import type { TocLobbyBannerValues } from './tocLobbyBanner';
import { buildTocLobbyBannerValues, lobbyTocFromLiveToc } from './tocLobbyBanner';

/**
 * The current coin's ToC lobby file and values from its page's `toc`, else in SC from the live SC ToC data (it swaps
 * in when that lands; the SC ToC decides the banner, so CC waits for the server), else `riveUrl: null` and the admin
 * banner shows; call from an observer.
 */
export const useTocLobbyBanner = (
  isMobile: boolean,
): { riveUrl: string | null; values: TocLobbyBannerValues | null } => {
  const { uiStore, coinTypeStore, tournamentStore, tournamentOfChampionsStore } = useStores();
  const { t, i18n } = useTranslation();
  const { lobby_cc, lobby_sc } = uiStore.bannerByPage;
  const pageToc = (coinTypeStore.coinType === 'GC' ? lobby_cc : lobby_sc)?.toc ?? null;
  const isScData = coinTypeStore.coinType === 'SC' && tournamentOfChampionsStore.tocCoinType === 'SC';
  const liveToc = isScData ? tournamentOfChampionsStore.tournamentOfChampions : null;
  const toc = useMemo(() => pageToc ?? lobbyTocFromLiveToc(liveToc), [pageToc, liveToc]);

  const isOn = uiStore.bannerAnimationFlag && tournamentStore.tournamentsFlag;
  const fileUrl = isOn && toc ? tocLobbyRiveUrl(toc, isMobile) : null;
  const riveUrl = fileUrl && !uiStore.failedTocLobbyRiveUrls.has(fileUrl) ? fileUrl : null;

  const values = useMemo(
    () => (riveUrl && toc ? buildTocLobbyBannerValues(toc, t, i18n.language) : null),
    [riveUrl, toc, t, i18n.language],
  );

  return { riveUrl, values };
};
